import React, { useEffect, useMemo, useRef, useState } from "react";
import { SB, KEY } from "../api/supabase";
import { DWORKS_STORE_BUILD } from "../constants/dworksConfig";
import { TxtArea } from "./ui";
import "./BetaFeedback.css";

const TYPES = [
  ["bug", "오류"],
  ["inconvenience", "불편함"],
  ["suggestion", "기능 제안"],
  ["question", "사용 방법 문의"],
];

function makeId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function extensionOf(file) {
  const raw = String(file?.name || "").split(".").pop().toLowerCase();
  return /^[a-z0-9]{1,8}$/.test(raw) ? raw : "jpg";
}

export default function BetaFeedback({ user, surface = "buyer", launcherVariant = "floating" }) {
  const feedbackTitle = DWORKS_STORE_BUILD ? "의견 보내기" : "베타 의견 보내기";
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("inconvenience");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [contactAllowed, setContactAllowed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const inputRef = useRef(null);
  const draftKey = user?.id ? `dworks:feedback-draft:${user.id}:${surface}` : "";

  useEffect(() => {
    if (!draftKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(draftKey) || "null");
      if (saved?.message?.trim()) {
        setType(saved.type || "inconvenience");
        setMessage(saved.message);
        setContactAllowed(saved.contactAllowed !== false);
      }
    } catch {}
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey) return;
    const cleanMessage = message.trim();
    if (!cleanMessage) {
      localStorage.removeItem(draftKey);
      return;
    }
    localStorage.setItem(draftKey, JSON.stringify({ type, message, contactAllowed }));
  }, [draftKey, type, message, contactAllowed]);

  const previews = useMemo(
    () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [files]
  );

  function close() {
    if (submitting) return;
    previews.forEach(({ url }) => URL.revokeObjectURL(url));
    setOpen(false);
    if (message.trim()) window.alert("작성 중인 내용이 임시 저장되었습니다.");
  }

  function clearDraft() {
    if (submitting) return;
    if ((message.trim() || files.length) && !window.confirm("작성 중인 의견을 모두 지울까요?")) return;
    previews.forEach(({ url }) => URL.revokeObjectURL(url));
    if (draftKey) localStorage.removeItem(draftKey);
    setMessage("");
    setFiles([]);
    setType("inconvenience");
    setContactAllowed(true);
    setNotice("작성 내용이 삭제되었습니다.");
  }

  function show() {
    setOpen(true);
    setNotice(message.trim() ? "임시 저장된 내용을 불러왔습니다." : "");
  }

  function chooseFiles(event) {
    const selected = Array.from(event.target.files || []);
    const images = selected.filter((file) => file.type.startsWith("image/"));
    if (images.length !== selected.length) {
      setNotice("이미지 파일만 첨부할 수 있습니다.");
    }
    const oversized = images.find((file) => file.size > 8 * 1024 * 1024);
    if (oversized) {
      setNotice("사진 1장당 최대 용량은 8MB입니다.");
      event.target.value = "";
      return;
    }
    setFiles((current) => [...current, ...images].slice(0, 3));
    event.target.value = "";
  }

  async function uploadImage(file, feedbackId, index) {
    const objectPath = `${user.id}/${feedbackId}/${index + 1}-${makeId()}.${extensionOf(file)}`;
    const response = await fetch(
      `${SB}/storage/v1/object/beta-feedback/${encodeURI(objectPath)}`,
      {
        method: "POST",
        headers: {
          apikey: KEY,
          Authorization: `Bearer ${user.token}`,
          "Content-Type": file.type || "application/octet-stream",
          "x-upsert": "false",
        },
        body: file,
      }
    );
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.message || data?.error || "사진 업로드에 실패했습니다.");
    }
    return objectPath;
  }

  async function submit(event) {
    event.preventDefault();
    const cleanMessage = message.trim();
    if (cleanMessage.length < 5) {
      setNotice("의견을 5자 이상 입력해 주세요.");
      return;
    }
    if (!user?.id || !user?.token) {
      setNotice("로그인 정보를 확인할 수 없습니다. 다시 로그인해 주세요.");
      return;
    }

    setSubmitting(true);
    setNotice("");
    try {
      const feedbackId = makeId();
      const attachmentPaths = [];
      for (let index = 0; index < files.length; index += 1) {
        attachmentPaths.push(await uploadImage(files[index], feedbackId, index));
      }

      const payload = {
        id: feedbackId,
        user_id: user.id,
        user_type: surface === "vendor" ? "vendor" : "buyer",
        feedback_type: type,
        message: cleanMessage,
        screen_path: window.location.pathname || "/",
        app_version: process.env.REACT_APP_VERSION || "1.0.0",
        device_info: {
          userAgent: navigator.userAgent,
          platform: navigator.platform || "",
          language: navigator.language || "",
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        },
        attachment_paths: attachmentPaths,
        contact_allowed: contactAllowed,
      };

      const response = await fetch(`${SB}/rest/v1/beta_feedback`, {
        method: "POST",
        headers: {
          apikey: KEY,
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Prefer: "return=representation",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || data?.error || "의견 접수에 실패했습니다.");
      }

      if (draftKey) localStorage.removeItem(draftKey);
      setMessage("");
      setFiles([]);
      setType("inconvenience");
      setNotice(`의견이 접수되었습니다. 접수번호: ${feedbackId.slice(0, 8).toUpperCase()}`);
    } catch (error) {
      setNotice(error?.message || "의견 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button className={launcherVariant === "text" ? "dw-more__feedback-link" : "beta-feedback-launcher"} type="button" onClick={show}>
        <span>{launcherVariant === "text" ? "의견 보내기" : feedbackTitle}</span>
        {launcherVariant === "text" && <span aria-hidden="true">›</span>}
      </button>
      {open && (
        <div className="beta-feedback-backdrop" role="presentation" onMouseDown={close}>
          <section
            className="beta-feedback-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="beta-feedback-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="beta-feedback-head">
              <div>
                <strong id="beta-feedback-title">{feedbackTitle}</strong>
                <p>불편한 점과 필요한 기능을 알려주세요.</p>
              </div>
              <button type="button" onClick={close} aria-label="닫기">×</button>
            </div>
            <form onSubmit={submit}>
              <label>
                의견 유형
                <select value={type} onChange={(event) => setType(event.target.value)}>
                  {TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label>
                의견 내용
                <TxtArea
                  val={message}
                  maxLength={2000}
                  rows={6}
                  onChange={setMessage}
                  ph="어느 화면에서 무엇이 불편했는지 적어주세요."
                />
                <span className="beta-feedback-count">{message.length}/2000</span>
              </label>
              <div className="beta-feedback-files">
                <span>스크린샷 (최대 3장)</span>
                <input ref={inputRef} hidden type="file" accept="image/*" multiple onChange={chooseFiles} />
                <button type="button" disabled={files.length >= 3} onClick={() => inputRef.current?.click()}>
                  사진 첨부
                </button>
                <div className="beta-feedback-previews">
                  {previews.map(({ file, url }, index) => (
                    <div key={`${file.name}-${index}`}>
                      <img src={url} alt={`첨부 사진 ${index + 1}`} />
                      <button type="button" onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}>삭제</button>
                    </div>
                  ))}
                </div>
              </div>
              <label className="beta-feedback-check">
                <input type="checkbox" checked={contactAllowed} onChange={(event) => setContactAllowed(event.target.checked)} />
                필요한 경우 등록된 연락처로 답변받기
              </label>
              <p className="beta-feedback-privacy">비밀번호, 결제정보, 주민등록번호 등 민감한 정보는 입력하지 마세요.</p>
              {notice && <div className="beta-feedback-notice" role="status">{notice}</div>}
              <div className="beta-feedback-actions">
                <button className="beta-feedback-clear" type="button" disabled={submitting} onClick={clearDraft}>작성 취소</button>
                <button className="beta-feedback-submit" type="submit" disabled={submitting}>
                  {submitting ? "접수 중..." : "의견 보내기"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </>
  );
}
